import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {Alert, Button, Col, Row, Space} from "antd";
import {StyledServiceButtonWrapper} from "../../../../styles/onboarding/onboarding";
import {useNavigate} from "react-router-dom";
import {
  editLiveEventBasicFormStepFormValidationSchema,
  EditLiveEventBasicFormStepFormValues,
} from "../schema-validation";
import {EditLiveEventFormValues} from "../types";
import {useGetLiveEventContext} from "../context/get-live-event";
import {LiveEventOfferingForm} from "../../../../components/offering-form/live-events";
import {ServiceFormLayout} from "../../../../calls/useCases/createService/service-form-layout";
import {useEditLiveEventHook} from "../hooks/edit-live-event";
import {useEffect, useMemo} from "react";
import {
  useCoverImagesWithFallback,
  mapDomainToEmbedded,
} from "../../../../features/media";
import {useOAuthCalendarResume} from "../../../../utils/google-calendar-resume";
import {IS_PWYW_ENABLED} from "../../../../services/config";
import {toSavedEditLiveEventFormValues} from "../saved-form-values";

export function EditLiveEventBasicForm() {
  const navigate = useNavigate();
  const {
    state: {liveEventData},
  } = useGetLiveEventContext();
  const {editLiveEvent, isEditingLiveEvent} = useEditLiveEventHook();
  const snapshot = useOAuthCalendarResume<EditLiveEventBasicFormStepFormValues>();

  const {coverImages} = useCoverImagesWithFallback({
    offeringType: "live-class",
    offeringId: liveEventData.id,
    embedded: liveEventData.cover_images,
  });

  const coverImagesForForm = useMemo(
    () => mapDomainToEmbedded(coverImages),
    [coverImages]
  );

  const formMethods = useForm<EditLiveEventBasicFormStepFormValues>({
    resolver: yupResolver(editLiveEventBasicFormStepFormValidationSchema),
    mode: "onSubmit",
    defaultValues: snapshot ?? {
      ...toSavedEditLiveEventFormValues(liveEventData, IS_PWYW_ENABLED),
      cover_images: liveEventData.cover_images,
    },
  });

  // console.log({liveEventData});

  useEffect(() => {
    formMethods.setValue("cover_images", coverImagesForForm);
    if (formMethods.watch("cover_images").length > 0) {
      formMethods.clearErrors("cover_images");
    } else {
      formMethods.trigger("cover_images");
    }
  }, [formMethods, coverImagesForForm]);

  const isFormError = Object.keys(formMethods.formState.errors).length > 0;
  const isGeneralInformationError =
    formMethods.formState.errors.name ||
    formMethods.formState.errors.description ||
    formMethods.formState.errors.coverImage;
  const isCoverImagesError = formMethods.formState.errors.cover_images;
  const isPricingError =
    formMethods.formState.errors.callPriceType || formMethods.formState.errors.currencies;
  const isSettingsError =
    formMethods.formState.errors.hideFromProfile ||
    formMethods.formState.errors.isUrlRedirect ||
    formMethods.formState.errors.redirectAfterPurchase ||
    formMethods.formState.errors.telegramGroupId ||
    formMethods.formState.errors.requestCustomerPhoneNumber ||
    formMethods.formState.errors.limitUnitsPerTransaction ||
    formMethods.formState.errors.purchaseLimit ||
    formMethods.formState.errors.url_redirect;

  // console.log({editformData: live-eventData});

  function onSubmit(values: EditLiveEventBasicFormStepFormValues) {
    const payload: EditLiveEventFormValues = {
      bookingQuestions: liveEventData.bookingQuestions
        ? liveEventData.bookingQuestions.map((q) => {
            if (
              q.answer_type === "checkbox" ||
              q.answer_type === "dropdown" ||
              q.answer_type === "radio-button"
            ) {
              return {
                question: q.question,
                required: q.required,
                answerType: q.answer_type,
                options: q.options.map((o) => ({
                  optionLabel: o,
                })),
              };
            } else {
              return {
                question: q.question,
                required: q.required,
                answerType: q.answer_type,
                options: [
                  {
                    optionLabel: "",
                  },
                ],
              };
            }
          })
        : [],
      coverImage: values.coverImage,
      // Handed over as the form holds them; the DTO does the base-currency split.
      currencies: values.currencies,
      description: values.description,
      hideFromProfile: values.hideFromProfile,
      id: liveEventData.id,
      isUrlRedirect: values.isUrlRedirect,
      name: values.name,
      callPriceType: values.callPriceType,
      requestCustomerPhoneNumber: values.requestCustomerPhoneNumber,
      limitUnitsPerTransaction: values.limitUnitsPerTransaction,
      purchaseLimit: values.purchaseLimit,
      url_redirect: values.url_redirect,
      redirectAfterPurchase: values.redirectAfterPurchase,
      telegramGroupId: values.telegramGroupId,
      attendeesType: values.attendeesType,
      callLocation: values.callLocation,
      locationAddress: values.locationAddress,
      locationLat: values.locationLat,
      locationLng: values.locationLng,
      isLocationPublic: values.isLocationPublic,
      callPrice: values.callPrice,
      callPriceCurrency: values.callPriceCurrency,
      classSeries: values.classSeries,
      closeRegistration: values.closeRegistration,
      customDuration: values.customDuration,
      duration: values.duration,
      startDate: values.startDate,
      startTime: values.startTime,
      topicType: values.topicType,
      attendees: values.attendees,
      customUrl: values.customUrl,
      enable_currency_conversion: true,
      selected_currencies: values.selected_currencies || [],
      cover_images: values.cover_images,
    };
    editLiveEvent(payload);
  }

  // console.log({editwatch: formMethods.watch(), editerrors: formMethods.formState.errors});
  return (
    <ServiceFormLayout>
      <FormProvider {...formMethods}>
        <form onSubmit={formMethods.handleSubmit(onSubmit as any)} autoComplete="off">
          <Space direction="vertical" size={16} style={{display: "flex"}}>
            <LiveEventOfferingForm
              isGeneralInformationError={!!isGeneralInformationError}
              isPricingError={!!isPricingError}
              isSettingsError={!!isSettingsError}
              isCoverImagesError={!!isCoverImagesError}
              formType="edit"
            />
            {isFormError && (
              <Alert
                message="Please fill in the required fields above, outlined in red"
                type="warning"
              />
            )}

            <Row gutter={[0, 16]}>
              <Col span={24} sm={12}>
                <StyledServiceButtonWrapper>
                  <Button
                    className="secondary-btn"
                    onClick={() => {
                      navigate("/calls");
                    }}
                    block
                    size="small"
                    type="default"
                  >
                    Back
                  </Button>
                  <Button
                    block
                    type="primary"
                    htmlType={"submit"}
                    size="small"
                    disabled={
                      Object.keys(formMethods.formState.errors).length > 0 ||
                      Object.values(formMethods.watch()).length < 1
                    }
                    loading={isEditingLiveEvent}
                  >
                    Update live event
                  </Button>
                </StyledServiceButtonWrapper>
              </Col>
            </Row>
          </Space>
        </form>
      </FormProvider>
    </ServiceFormLayout>
  );
}
